/* eslint-disable newline-per-chained-call */
import { z } from 'zod';

// Helper function to validate URL format
const isValidUrl = (urlString: string): boolean => {
  try {
    return Boolean(new URL(urlString));
  } catch {
    // Try with https prefix
    try {
      return Boolean(new URL(`https://${urlString}`));
    } catch {
      return false;
    }
  }
};

// Define the base schema for new ad set
const adSetNewBase = z.object({
  type: z.literal('new'),
  name: z.string().min(2, { message: 'Ad set name must be at least 2 characters.' }),
  billingEvent: z
    .enum([
      'APP_INSTALLS',
      'CLICKS',
      'IMPRESSIONS',
      'LINK_CLICKS',
      'LISTING_INTERACTION',
      'OFFER_CLAIMS',
      'PAGE_LIKES',
      'POST_ENGAGEMENT',
      'PURCHASE',
      'THRUPLAY',
      'VIDEO_VIEWS',
    ])
    .optional(),
  bidAmount: z.number().int().min(0).max(1000000).optional(),
  dailyBudget: z.number().positive().max(1000000).optional(),
  lifetimeBudget: z.number().positive().max(100000000).optional(),
  startTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Start time is not a valid date' })
      .optional(),
  ),
  stopTime: z.preprocess(
    (val) => (val ? new Date(val as string) : undefined),
    z
      .date()
      .refine((d) => !isNaN(d.getTime()), { message: 'Stop time is not a valid date' })
      .optional()
      .refine((val) => !val || val > new Date(), {
        message: 'Stop time must be null or a date in the future',
      }),
  ),
  targeting: z.object({
    geo_locations: z.object({
      countries: z.array(z.string()).optional(),
    }),
  }),
  status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
});

export const boostPostSchema = z
  .object({
    adAccount: z.object({
      id: z.string().min(1, { message: 'Please select an ad account.' }),
    }),
    post: z.object({
      postExternalId: z.string().min(1, { message: 'Please select a post.' }),
      platform: z.literal('instagram'),
    }),
    campaign: z.discriminatedUnion('type', [
      z.object({
        type: z.literal('existing'),
        campaignId: z.string().min(1, { message: 'Please select a campaign.' }),
        status: z.string().optional(),
      }),
      z.object({
        type: z.literal('new'),
        name: z.string().min(2, { message: 'Campaign name must be at least 2 characters.' }),
        objective: z.enum([
          'OUTCOME_APP_PROMOTION',
          'OUTCOME_AWARENESS',
          'OUTCOME_ENGAGEMENT',
          'OUTCOME_LEADS',
          'OUTCOME_SALES',
          'OUTCOME_TRAFFIC',
        ]),
        status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
      }),
    ]),
    adSet: z
      .discriminatedUnion('type', [
        z.object({
          type: z.literal('existing'),
          id: z.string().min(1, { message: 'Please select an ad set.' }),
          status: z.string().optional(),
        }),
        adSetNewBase,
      ])
      .refine(
        (data) => {
          if (data.type === 'new') {
            const hasDailyBudget = data.dailyBudget !== undefined;
            const hasLifetimeBudget = data.lifetimeBudget !== undefined;
            return (hasDailyBudget && !hasLifetimeBudget) || (!hasDailyBudget && hasLifetimeBudget);
          }
          return true;
        },
        {
          message: 'Either dailyBudget or lifetimeBudget must be set, but not both',
          path: ['lifetimeBudget'],
        },
      )
      .refine(
        (data) => {
          if (data.type === 'new') {
            const hasDailyBudget = data.dailyBudget !== undefined;
            const hasLifetimeBudget = data.lifetimeBudget !== undefined;
            return (hasDailyBudget && !hasLifetimeBudget) || (!hasDailyBudget && hasLifetimeBudget);
          }
          return true;
        },
        {
          message: 'Either dailyBudget or lifetimeBudget must be set, but not both',
          path: ['dailyBudget'],
        },
      )
      .refine(
        (data) => {
          if (data.type === 'new') {
            const hasLifetimeBudget = data.lifetimeBudget !== undefined;
            const missingStopTime = hasLifetimeBudget && !data.stopTime;
            return !missingStopTime;
          }
          return true;
        },
        {
          message: 'Stop time must be set if lifetimeBudget is set',
          path: ['stopTime'],
        },
      )
      .refine(
        (data) => {
          if (data.type === 'new') {
            const invalidTime =
              data.startTime &&
              data.stopTime &&
              (data.startTime > data.stopTime || Number(data.stopTime) - Number(data.startTime) < 24 * 60 * 60 * 1000);
            return !invalidTime;
          }
          return true;
        },
        {
          message: 'Start time must be 24 hours before stop time',
          path: ['startTime', 'stopTime'],
        },
      ),
    // Add will always be new
    ad: z.object({
      name: z.string().min(2, { message: 'Ad name must be at least 2 characters.' }),
      status: z.enum(['ACTIVE', 'PAUSED']).optional().default('ACTIVE'),
      callToActionType: z.enum([
        'ADD_TO_CART',
        'APPLY_NOW',
        'ASK_ABOUT_SERVICES',
        'ASK_FOR_MORE_INFO',
        'AUDIO_CALL',
        'BOOK_A_CONSULTATION',
        'BOOK_NOW',
        'BOOK_TRAVEL',
        'BUY',
        'BUY_NOW',
        'BUY_TICKETS',
        'BUY_VIA_MESSAGE',
        'CALL',
        'CALL_ME',
        'CALL_NOW',
        'CHAT_WITH_US',
        'CONFIRM',
        'CONTACT',
        'CONTACT_US',
        'DONATE',
        'DONATE_NOW',
        'DOWNLOAD',
        'EVENT_RSVP',
        'FIND_A_GROUP',
        'FIND_YOUR_GROUPS',
        'FOLLOW_NEWS_STORYLINE',
        'FOLLOW_PAGE',
        'FOLLOW_USER',
        'GET_A_QUOTE',
        'GET_DIRECTIONS',
        'GET_IN_TOUCH',
        'GET_OFFER',
        'GET_OFFER_VIEW',
        'GET_PROMOTIONS',
        'GET_QUOTE',
        'GET_SHOWTIMES',
        'GET_STARTED',
        'INQUIRE_NOW',
        'INSTAGRAM_MESSAGE',
        'INSTALL_APP',
        'INSTALL_MOBILE_APP',
        'JOIN_CHANNEL',
        'LEARN_MORE',
        'LIKE_PAGE',
        'LISTEN_MUSIC',
        'LISTEN_NOW',
        'MAKE_AN_APPOINTMENT',
        'MESSAGE_PAGE',
        'MOBILE_DOWNLOAD',
        'NO_BUTTON',
        'OPEN_INSTANT_APP',
        'OPEN_LINK',
        'ORDER_NOW',
        'PAY_TO_ACCESS',
        'PLAY_GAME',
        'PLAY_GAME_ON_FACEBOOK',
        'PURCHASE_GIFT_CARDS',
        'RAISE_MONEY',
        'RECORD_NOW',
        'REFER_FRIENDS',
        'REQUEST_TIME',
        'SAY_THANKS',
        'SEE_MORE',
        'SELL_NOW',
        'SEND_A_GIFT',
        'SEND_GIFT_MONEY',
        'SEND_UPDATES',
        'SHARE',
        'SHOP_NOW',
        'SIGN_UP',
        'SOTTO_SUBSCRIBE',
        'START_ORDER',
        'SUBSCRIBE',
        'SWIPE_UP_PRODUCT',
        'SWIPE_UP_SHOP',
        'UPDATE_APP',
        'USE_APP',
        'USE_MOBILE_APP',
        'VIDEO_ANNOTATION',
        'VIDEO_CALL',
        'VIEW_CART',
        'VIEW_CHANNEL',
        'VIEW_INSTAGRAM_PROFILE',
        'VIEW_IN_CART',
        'VIEW_PRODUCT',
        'VISIT_PAGES_FEED',
        'WATCH_LIVE_VIDEO',
        'WATCH_MORE',
        'WATCH_VIDEO',
        'WHATSAPP_MESSAGE',
        'WOODHENGE_SUPPORT',
      ]),
      identityDisplayMode: z.enum(['CREATOR_AND_BRAND', 'CREATOR_ONLY', 'DYNAMIC']).default('CREATOR_AND_BRAND'),
      callToActionUrl: z.string().optional(),
      useUtmParameters: z.boolean().optional().default(false),
      utmParameters: z
        .object({
          utmSource: z
            .string()
            .optional()
            .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
              message: "UTM Source cannot contain '#', '=' or space characters",
            }),
          utmMedium: z
            .string()
            .optional()
            .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
              message: "UTM Medium cannot contain '#', '=' or space characters",
            }),
          utmCampaign: z
            .string()
            .optional()
            .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
              message: "UTM Campaign cannot contain '#', '=' or space characters",
            }),
          utmContent: z
            .string()
            .optional()
            .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
              message: "UTM Content cannot contain '#', '=' or space characters",
            }),
          utmTerm: z
            .string()
            .optional()
            .refine((val) => !val || (!val.includes('#') && !val.includes('=') && !val.includes(' ')), {
              message: "UTM Term cannot contain '#', '=' or space characters",
            }),
        })
        .optional(),
      customUtmParameters: z
        .array(
          z.object({
            key: z.string().optional(),
            value: z.string().optional(),
          }),
        )
        .optional(),
    }),
  })
  .superRefine((val, ctx) => {
    if (val.adSet?.type === 'existing' && val.campaign.type !== 'existing') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You can only select an existing Ad Set if you have selected an existing Campaign.',
        path: ['adSet', 'type'],
      });
    }

    // EPD-1644: Validate callToActionUrl only when callToActionType is not NO_BUTTON
    if (val.ad.callToActionType !== 'NO_BUTTON') {
      if (!val.ad.callToActionUrl || val.ad.callToActionUrl.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid URL for the Call To Action.',
          path: ['ad', 'callToActionUrl'],
        });
      } else if (!isValidUrl(val.ad.callToActionUrl)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid URL for the Call To Action.',
          path: ['ad', 'callToActionUrl'],
        });
      }
    }
  });

export type BoostPostFormValues = z.infer<typeof boostPostSchema>;
